import React from 'react';
import DtvBox from '@directv-digital/dds.ui.dtv-box';
import DtvTabs from "@directv-digital/dds.ui.dtv-tabs";
import DtvText, { IDtvTextProps } from '@directv-digital/dds.ui.dtv-text';
import ErrorDisplay from '@directv-digital/dds.ui.error-display';
import { Analytics, fireTabHandleChange } from '@directv-digital/dds.services.analytics-service';
import Avatar from "@mui/material/Avatar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import Box from "@mui/material/Box";

export type DtvExternalServiceComparisonChartTabsProps = {
  /**
  * CI configurable: Yes
  * DESC: To Configure the id of component
  */
  id: string;
  /**
  * CI configurable: Yes
  * DESC: To Configure the the tabs for the component
  */
  tabsLabels: TabsLabel[];
  /**
  * CI configurable: Yes
  * DESC: To add analytics for the component to track the user interaction
  */
  analytics?: Analytics;
  /**
  * CI configurable: Yes
  * DESC: To add heading for the component, uses DtvText modal
  */
  heading?: IDtvTextProps[] | IDtvTextProps;
  /**
  * CI configurable: Yes
  * DESC: To to remove extra spacing between rows and cells
  */
  removeSpaceAround?: boolean;
  /**
  * CI configurable: Yes
  * DESC: To configure bottom border inside dtv-tabs component
  */
  noBorder?: boolean;
  /**
  * CI configurable: No
  * DESC: Internal prop to determine if the component is above the fold
  */
  aboveTheFold?: boolean;
  /**
  * CI configurable: Yes
  * DESC: Sets the aria-label on the tabs list when no visible heading is present
  */
  tabsAriaLabel?: string;
};

interface TabsLabel {
  /**
  * CI configurable: Yes
  * DESC: Sets the label seen on the tab
  */
  label: {
    content: string;
  };
   /**
  * CI configurable: Yes
  * DESC: Configures table shown under each tab when associated tab is clicked
  */
  table: TabsTable[] | TabsTable;
}

interface TabsTable {
   /**
  * CI configurable: Yes
  * DESC: Congigures columns to be shown in table
  */
  columns?: any;
   /**
  * CI configurable: Yes
  * DESC: Configures rows to be shown in table
  */
  rows?: any;
   /**
  * CI configurable: Yes
  * DESC: Sets the column background colro
  */
  backgroundColor?: any;
   /**
  * CI configurable: Yes
  * DESC: Sets a key to determine which column is considere internal
  */
  internalServiceKey?: string;
   /**
  * CI configurable: Yes
  * DESC: Sets the sets the aria-label on each table
  */
  ariaLabel?: string;
}

const mapStatusToIcon = {
  YES_GREEN:
    "https://www.directv.com/dtvassets/global/icons/2022/Control-Check-green.svg",
  YES_GRAY:
    "https://www.directv.com/dtvassets/global/icons/2022/Control-Check-gray.svg",
  NO: "https://www.directv.com/dtvassets/global/icons/2022/Control-Close-red.svg",
  NA: "https://www.directv.com/dtvassets/global/icons/2022/NEW_Control-Minus-gray.svg"
};

export const DtvExternalServiceComparisonChartTabs = (props: DtvExternalServiceComparisonChartTabsProps) => {
  try {
    const {
      heading,
      tabsLabels,
      analytics,
      id,
      tabsAriaLabel
    } = props;

    const tabHeading = Array.isArray(heading) ? heading : [heading];
    const headingId = id ? `${id}-heading` : undefined;
    const hasVisibleHeading = Boolean(tabHeading?.[0]?.content) && Boolean(headingId);

    const getValue = (obj, prop) => {
      const result = Array.isArray(obj) ? obj?.[0]?.[prop] : obj?.[prop]

      return result;
    }

    const getColorForTab = (tabIndex: number, index: number) => {
      const table = tabsLabels?.[tabIndex]?.table;

      return Array.isArray(table) ? table?.[0]?.backgroundColor?.[index]?.color : [table]?.[0]?.backgroundColor?.[index]?.color
    };

    const internalServiceIndex = (columns: any[], internalServiceKey: string | undefined) => columns?.findIndex?.(column => (column?.key === internalServiceKey))

    const onTabChange = tabIndex => {
      const linkName = tabsLabels?.[tabIndex]?.label?.content
      const analyticsData = {
        linkName: `/${linkName?.toLowerCase()}`,
        linkPosition: 'external-service-comparison-chart-tabs',
        linkDestination: `/tab/${linkName}`
      }
      fireTabHandleChange(analyticsData);
    };

    return (
      <DtvBox>
        <DtvBox>
          {tabHeading?.[0]?.content && (
            <DtvBox
              sx={{
                textAlign: "center",
                mb: {
                  xl: 20,
                  md: 15,
                  sm: 12,
                  xs: 10
                }
              }}
            >
              <DtvText
                id={headingId}
                content={tabHeading?.[0]?.content}
                variant="subhead1medium"
                component={tabHeading?.[0]?.component || "div"}
                color="jet.main"
              />
            </DtvBox>
          )}
        </DtvBox>
        <DtvBox>
          {tabsLabels?.length > 0 && (
            <DtvTabs
              tabVariant={"externalservicecomparisoncharttab"}
              tabsLabel={tabsLabels?.slice(0, 4)}
              id={`${id}-tab-panel`}
              position={"center"}
              analytics={analytics}
              onChange={onTabChange}
              aria-labelledby={hasVisibleHeading ? headingId : undefined}
              aria-label={hasVisibleHeading ? undefined : (tabsAriaLabel || "Service comparison tabs")}
            >
              {tabsLabels?.slice(0, 4).map((tab, tabIndex) => {
                const { table } = tab;
                const tabTable = Array.isArray(table) ? table : [table];
                const { columns, rows, backgroundColor, internalServiceKey, ariaLabel } = tabTable?.[0] ?? {};
                return (
                  <DtvBox
                    key={internalServiceKey ?? tabIndex}
                    sx={{
                      overflowX: "auto"
                    }}
                  >
                    <TableContainer
                      sx={{
                        minWidth: {
                          xs: `calc(52% + 33% * ${columns?.length - 1})`,
                          sm: "auto"
                        },
                        overflowX: "unset",
                        px: {
                          xl: 12,
                          md: 8,
                          xs: 0,
                          backgroundColor: "#ffff"
                        },
                        paddingRight: { // 6px offsets scrollbar
                          xs: "6px",
                          sm: "10px",
                          md: "14px"
                        },
                        paddingBottom: { // 6px offsets scrollbar
                          xs: "16px",
                          sm: "30px",
                          md: "0px"
                        }
                      }}
                    >
                      <Table aria-label={ariaLabel} sx={{
 tableLayout: "fixed",
borderCollapse: "separate"
}}>
                        <TableHead>
                          <TableRow>
                            {columns?.map((column, columnIndex, arr) => {
                              const isLastCell = columnIndex === arr?.length - 1;
                              return (
                                <TableCell
                                  key={`tab-${tabIndex}-col-${columnIndex}`}
                                  role={columnIndex === 0 ? "rowheader" : "columnheader"}
                                  aria-hidden={columnIndex === 0}
                                  sx={{
                                    textAlign: "center",
                                    border: "none",
                                    ...(!isLastCell ?
                                      {
                                        borderRight: "1px solid",
                                        borderRightColor: "gray.100"
                                      } :
                                      null),
                                    ...(columnIndex === 0 ?
                                      {
                                        position: {
                                          xs: "sticky"
                                        },
                                        left: {
                                          xs: 0
                                        },
                                        zIndex: 20,
                                        background: "#ffff",
                                        width: {
                                          xs: "136px",
                                          sm: "241px",
                                          md: "368px"
                                        }
                                      } :
                                      {
                                        width: {
                                          xs: "auto"
                                        }
                                      }),
                                    backgroundColor: backgroundColor?.[columnIndex]?.color ?
                                      backgroundColor?.[columnIndex]?.color :
                                      column?.key === internalServiceKey ?
                                        "lapis.light" :
                                        "#ffff"
                                  }}
                                >
                                   {column?.productName && (
                                  <Box sx={{
 padding: "16px",
border: "none",
display: "flex",
alignItems: "center",
justifyContent: "center"
}}>
                                  <Box
                                    component="img"
                                    src={column?.productName?.image || column?.productName?.[0]?.image}
                                    alt={column?.productName?.altname || column?.productName?.[0]?.altname ||  column?.productName?.content}
                                    sx={{
                                      height: {
                                        xs: "12px",
                                        sm: "16px",
                                        md: "28px"
                                      },
                                      objectFit: "contain",
                                      maxWidth: "100%",
                                      maxHeight: "100%"
                                    }}
                                  />
                                </Box>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows?.map((row, rowIndex) => (
                              <TableRow key={`tab-${tabIndex}-row-${rowIndex}`}>
                                {row?.cells?.map((cell, cellIndex, arr) => {
                                  const status = cell?.available?.toUpperCase();
                                  const isInternalService =
                                    cellIndex === internalServiceIndex(columns, internalServiceKey);
                                  const iconUrl =
                                    status === "YES" ?
                                      isInternalService ?
                                        mapStatusToIcon?.YES_GREEN :
                                        mapStatusToIcon?.YES_GRAY :
                                      mapStatusToIcon?.[status?.toUpperCase()];
                                  const isLastCell = cellIndex === arr?.length - 1;
                                  return (
                                    <TableCell
                                      key={`tab-${tabIndex} and ${rowIndex} and ${cellIndex}`}
                                      sx={{
                                        border: "none",
                                        verticalAlign: cell?.channelDetails ? 'baseline' : 'middle',
                                        borderTop: "1px solid",
                                        ...(!isLastCell ?
                                          {
                                            borderRight: "1px solid",
                                            borderRightColor: "gray.100"
                                          } :
                                          null),

                                        borderTopColor: "gray.100",
                                        backgroundColor: backgroundColor?.[cellIndex]?.color ?
                                          getColorForTab(tabIndex, cellIndex) :
                                          isInternalService ?
                                            "lapis.light" :
                                            "",

                                        padding: "24px 16px !important",

                                        ...(cellIndex === 0 ?
                                          {
                                            position: {
                                              xs: "sticky"
                                            },
                                            left: {
                                              xs: 0
                                            },
                                            zIndex: 1,
                                            background: "#ffff"
                                          } :
                                          {
                                            width: {
                                              xs: "auto"
                                            }
                                          })
                                      }}
                                    >
                                      {cell?.available && (
                                        <Avatar
                                          imgProps={{ loading: props.aboveTheFold ? "eager" : "lazy" }}
                                          src={iconUrl}
                                          alt={cell?.altText}
                                          sx={{
                                            width: {
 xs: "24px",
md: "32px"
},
                                            height: {
 xs: "24px",
md: "32px"
},
                                            margin: "auto"
                                          }}
                                        />
                                      )}
                                      {cell?.title && (
                                        <DtvText
                                        sx={{
                                          [DtvTheme.breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                                            hyphens: "auto"
                                          },
                                          fontSize: "16px"
                                        }}
                                          content={getValue(cell?.title, "content") || ""}
                                          cta={{
                                            position: "block",
                                            sx: {
                                              mt: 2,
                                              textAlign: "center",
                                              display: "block",
                                              color: "jet.main"
                                            },
                                            ...cell?.cta?.[0],
                                            label: (
                                              getValue(cell?.cta, "label") ?
                                                <DtvText
                                                  content={getValue(cell?.cta, "label")}
                                                  variant="smalltext"
                                                /> : ''
                                            )
                                          }}
                                          tooltip={{
                                            sx: {
                                              fontSize: "12px",
                                              marginLeft: "5px",
                                              verticalAlign: "bottom"
                                            },
                                            content: getValue(cell?.tooltip, "content")
                                          }}
                                          variant="body2medium"
                                          component={getValue(cell?.title, "component")  || "div"}
                                          color="jest.main"
                                        />
                                      )}
                                      {cell?.description && (
                                        <DtvText
                                          content={getValue(cell?.description, "content")}
                                          cta={getValue(cell?.description, "tooltip")}
                                          tooltip={getValue(cell?.description, "tooltip")}
                                          variant="body2medium"
                                          component={getValue(cell?.description, "component") || "div"}
                                          color="jest.main"
                                          sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            textAlign: "center",
                                            fontSize: "16px",
                                            ...(cell?.available ? { mt: "1em" } : {})
                                          }}
                                        />
                                      )}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </DtvBox>
                )
              })}
            </DtvTabs>
          )}
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    return (
      <ErrorDisplay error={error} component="DTV External Service Comparison Chart with tabs" />
    );
  }
}
